import { useCallback, useMemo, useState, useEffect } from "react";
import useGetReportJobDetails from "hooks/organization/reports/types/useGetReportJobDetails";
import { capitalCase } from "change-case";
import JobModal from "components/JobModal/JobModal";
import usePaginatedResources from "hooks/usePaginatedResources";

export default function useReportTableJobDetails(startDate, endDate, companyId = "", jobId = "") {
  const [openModal, setOpenModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const [getReportJobDetails, { data, loading, error }] = useGetReportJobDetails(false); // No success message for filtering/pagination

  const handleOpenModal = useCallback((jobId: string) => {
    setSelectedJobId(jobId);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedJobId(null);
    setOpenModal(false);
  }, []);

  const paginate = useCallback(
    ({ page = 1, first = 25 } = {}) => {
      const variables = {
        startDate,
        endDate,
        companyId,
        jobId,
        page,
        first,
      };
      getReportJobDetails({ variables });
    },
    [startDate, endDate, companyId, jobId, getReportJobDetails]
  );

  const pagination = usePaginatedResources({
    paginate,
    paginatorInfo: data?.getReportJobDetails?.paginatorInfo,
  });

  const columns = useMemo(() => {
    return [
      {
        Header: "Job ID",
        accessor: "job_external_id",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Job Phase Id",
        accessor: "job_id",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Proposal Id",
        accessor: "proposal_external_id",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposal Stage Id",
        accessor: "proposal_stage_id",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => value ?? "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Started at",
        accessor: "starts_at",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Ends at",
        accessor: "ends_at",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposed Cost Material",
        accessor: "proposed_cost_material",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Cost Material",
        accessor: "actual_cost_material",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposed Cost Equipment",
        accessor: "proposed_cost_equipment",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Cost Equipment",
        accessor: "actual_cost_equipment",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposed Net Margin",
        accessor: "proposed_net_margin",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Cost Labor",
        accessor: "actual_cost_labour",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposed Cost Additional",
        accessor: "proposed_cost_additional",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Cost Additional",
        accessor: "actual_cost_additional",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposed Cost Direct",
        accessor: "proposed_cost_direct",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Cost Direct",
        accessor: "actual_cost_direct",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposed Cost Overhead",
        accessor: "proposed_cost_overhead",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Cost Overhead",
        accessor: "actual_cost_overhead",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposed Cost Indirect",
        accessor: "proposed_cost_indirect",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Cost Indirect",
        accessor: "actual_cost_indirect",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposed Cost Total",
        accessor: "proposed_cost_total",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Cost Total",
        accessor: "actual_cost_total",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposed Profit",
        accessor: "proposed_profit",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Profit",
        accessor: "actual_profit",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },

      {
        Header: "Proposed Profit Percentage",
        accessor: "proposed_profit_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Profit Percentage",
        accessor: "actual_profit_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposed Gross Margin",
        accessor: "proposed_gross_margin",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Gross Margin",
        accessor: "actual_gross_margin",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposed Gross Margin Percentage",
        accessor: "proposed_gross_margin_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Gross Margin Percentage",
        accessor: "actual_gross_margin_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposed Operating Margin",
        accessor: "proposed_operating_margin",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Operating Margin",
        accessor: "actual_operating_margin",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposed Operating Margin Percentage",
        accessor: "proposed_operating_margin_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Operating Margin Percentage",
        accessor: "actual_operating_margin_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },

      {
        Header: "Actual Net Margin",
        accessor: "actual_net_margin",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposed Net Margin Percentage",
        accessor: "proposed_net_margin_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Net Margin Percentage",
        accessor: "actual_net_margin_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposed Overall Total",
        accessor: "proposed_overall_total",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Overall Total",
        accessor: "actual_overall_total",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Notes",
        accessor: "notes",
        Cell: ({ value }) => (value ? <div dangerouslySetInnerHTML={{ __html: value }} /> : ""),
        disableSortBy: true,
      },
      {
        Header: "Client Name",
        accessor: "company_name",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Job Stage Name",
        accessor: "proposal_stage_name",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Job Stage Address",
        accessor: "proposal_stage_address",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Job Stage City",
        accessor: "proposal_stage_city",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Job Stage State",
        accessor: "proposal_stage_state",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Crew Name",
        accessor: "crew_name",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Crew Display Name",
        accessor: "crew_display_name",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },

      {
        Header: "Product Type",
        accessor: "organization_product_type_name",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Product Job Size",
        accessor: "proposal_product_job_size",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Product Job Duration",
        accessor: "proposal_product_job_duration",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Product Material Duration",
        accessor: "proposal_product_material_duration",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Product Material Type",
        accessor: "proposal_product_material_type",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Product Material Used (From Job Costing)",
        accessor: "proposal_product_material_used",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
    ];
  }, [handleOpenModal]);

  const tableData = useMemo(() => {
    return {
      columns,
      rows: data?.getReportJobDetails?.data || [],
      modal: <JobModal jobId={selectedJobId} open={openModal} onClose={handleCloseModal} />,
    };
  }, [columns, data, selectedJobId, openModal, handleCloseModal]);

  return {
    columns,
    data,
    loading,
    error,
    tableData,
    pagination,
    getReportJobDetails: paginate,
    metadata: data?.getReportJobDetails?.metadata,
  } as const;
}
